/**
 * Safe accessors for Lovable asset pointer files (`*.asset.json`).
 *
 * A pointer file can end up empty (`{}`) when an upload fails or is
 * interrupted. Vite then types the import as `{}`, and any direct `.url`
 * access becomes a TS2339 build error ("Property 'url' does not exist on
 * type '{}'"). Always read pointers through these helpers instead of
 * touching `.url` directly so a broken pointer degrades at runtime instead
 * of breaking the build.
 */

export type AssetPointer = {
  url?: string;
  original_filename?: string;
  content_type?: string;
  size?: number;
};

/** Returns the pointer URL, or "" when the pointer is empty/invalid. */
export function assetUrl(pointer: unknown): string {
  const url = (pointer as AssetPointer | null | undefined)?.url;
  return typeof url === "string" ? url.trim() : "";
}

/** True when the pointer has a usable URL. */
export function hasAsset(pointer: unknown): boolean {
  return assetUrl(pointer).length > 0;
}

/** First pointer in the list that has a usable URL, else "". */
export function firstAssetUrl(...pointers: unknown[]): string {
  for (const p of pointers) {
    const url = assetUrl(p);
    if (url) return url;
  }
  return "";
}

/**
 * Builds a responsive `srcSet` string, skipping pointers with no URL.
 * Returns "" when nothing is usable so callers can omit the <source>.
 */
export function assetSrcSet(entries: Array<[pointer: unknown, width: number]>): string {
  return entries
    .map(([pointer, width]) => [assetUrl(pointer), width] as const)
    .filter(([url]) => url.length > 0)
    .map(([url, width]) => `${url} ${width}w`)
    .join(", ");
}
